/*
  The page's own scrolling, smoothed.

  Lenis moves the real scroll position — it does not slide the content under a
  faked scrollbar the way the older smooth-scroll libraries did — so the sticky
  header, the screens that hold themselves in place and every ScrollTrigger on
  the site keep working exactly as they are. All that changes is how the page
  gets to a scroll position: a notch of the wheel is carried across a handful of
  frames instead of landing in one.

  Deliberately restrained. Smoothing is paid for in responsiveness, and past a
  certain point a page stops reading as elegant and starts reading as slow. LERP
  is the dial: the share of the remaining distance covered each frame. Higher
  arrives sooner, 1 is no smoothing at all.

  The wheel only. Touch keeps the browser's own scrolling — a smoothed phone
  scroll fights the address bar and reads as lag — and anyone who asked for less
  movement gets the scroll untouched.
*/
(function () {
  /* The share of the remaining distance covered each frame. */
  var LERP = 0.08;
  /* Seconds a link that travels to a point on the page takes to get there. */
  var TRAVEL = 0.8;
  /* Rows that scroll sideways and must keep a sideways swipe to themselves. */
  var SIDEWAYS = '[data-md-track]';

  var lenis = null;

  function tick(time) {
    /* GSAP counts in seconds, Lenis in milliseconds. */
    lenis.raf(time * 1000);
  }

  function start() {
    if (lenis || !window.Lenis) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    lenis = new Lenis({
      lerp: LERP,
      smoothWheel: true,
      /* The phone is left alone. */
      syncTouch: false,
      /* Anything that scrolls inside the page — the menu, the cart, the
         filters, a modal — keeps its own scrolling, and hands the page back
         over once it reaches its end, the way the browser would. */
      allowNestedScroll: true
    });

    /* The whole site's scroll animation already runs on GSAP, so one clock
       drives both: Lenis moves the page, and ScrollTrigger reads the position
       Lenis arrived at rather than the one the wheel asked for. Without this
       the two run a frame apart and anything scrubbed judders. */
    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
    } else {
      requestAnimationFrame(function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      });
    }

    window.hogoLenis = lenis;
  }

  /*
    One way in for everything that travels to a point on the page — the menu's
    anchors, the reminder link, the search field on a phone. With the smoothing
    on, asking the browser for its own smooth scroll would set two hands pulling
    the page at once, which reads as a jump or as a stall halfway.

    Accepts what scrollIntoView accepts, plus a number for a plain distance from
    the top, and falls back to the browser when the smoothing is off.
  */
  window.hogoScrollTo = function (target, options) {
    var opts = options || {};
    var block = opts.block || 'start';
    var behavior = opts.behavior || 'smooth';

    if (typeof target === 'string') target = document.querySelector(target);
    if (target === null || target === undefined) return;

    if (typeof target === 'number') {
      if (lenis && behavior === 'smooth') {
        lenis.scrollTo(target, { duration: TRAVEL });
      } else {
        window.scrollTo({ top: target, behavior: behavior });
      }
      return;
    }

    if (lenis && behavior === 'smooth') {
      var offset = 0;

      /* Lenis lands the target at the top of the screen; centring is that
         landing pulled back by the empty half-screen above it. */
      if (block === 'center') {
        offset = -Math.max(0, (window.innerHeight - target.getBoundingClientRect().height) / 2);
      }

      lenis.scrollTo(target, { offset: offset, duration: TRAVEL });
      return;
    }

    target.scrollIntoView({ behavior: behavior, block: block });
  };

  /*
    A sideways row inside a page that scrolls downwards. Lenis takes the wheel
    event to move the page, which would leave a trackpad swipe across such a row
    doing nothing at all; handing the sideways half of the gesture back to the
    row keeps both halves of it. A purely sideways swipe never reaches here —
    Lenis lets those through untouched.
  */
  document.addEventListener(
    'wheel',
    function (event) {
      if (!lenis || event.deltaY === 0) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      if (!(event.target instanceof Element)) return;

      var row = event.target.closest(SIDEWAYS);
      if (row) row.scrollLeft += event.deltaX;
    },
    { passive: true }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* The theme editor swaps a section out from under the page, which changes how
     tall the page is — and with it every distance the scroll animations hold. */
  document.addEventListener('shopify:section:load', function () {
    if (lenis) lenis.resize();
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  /*
    Holding the page still while the menu or the cart is open, one way for
    both (custom-header.js, cart-drawer.js).

    The page's own scrollbar goes away with the overflow on <html> (never on
    <body>: there it would make the body its own scroll box and the sticky
    header would leave its place), which is also what reliably stops a finger
    on an iPhone. Where that scrollbar takes up room — a mouse on a computer —
    the page is padded by exactly its width, so nothing moves sideways; the
    header stretches over that strip (header-custom-rgd.liquid) and the dark
    overlay covers it like the rest of the page. Scrollbars that float over the
    page measure zero and need nothing.
  */
  var locked = false;

  window.hogoPageLock = {
    lock: function () {
      if (locked) return;
      locked = true;

      var root = document.documentElement;
      root.style.setProperty('--page-lock-gap', window.innerWidth - root.clientWidth + 'px');
      root.classList.add('page-locked');

      if (lenis) lenis.stop();
    },

    unlock: function () {
      if (!locked) return;
      locked = false;

      var root = document.documentElement;
      root.classList.remove('page-locked');
      root.style.removeProperty('--page-lock-gap');

      if (lenis) lenis.start();
    },
  };
})();
